import { useEffect, useRef, useState } from 'react';
import { t } from '@lingui/macro';
import { bool, node, string } from 'prop-types';
import { ErrorBoundary } from 'react-error-boundary';
import { isEmpty, isEqual } from 'lodash';

import FilterControlContext, { useFilterControlContextValue } from '../filter/FilterControl';
import ErrorMessage from '../../error-message/ErrorMessage';
import { usePageState } from '../../../stores/PageStateStore';
import useSize from '../../../utilities/UseSize';
import useIsPrinting from '../../../utilities/PrintUtilities';
import { useAllowDrillIn, useGroupPages } from '../../../stores/SessionStore';
import { usePlmMainMenuPages } from '../../side-nav/menus/PlmMainMenuPages';
import { useErrorWrapper } from '../../../services/FormErrorService';
import useUiAlerts, { INFO, createAlertConfig } from '../../../stores/AlertsStore';
import { PageContext } from './PageUtils';
import { useDefaultPage } from '../../side-nav/DefaultPage';
import { noAccessPageId } from '../../pages';
import { NEW_DASHBOARD_ID } from '../dashboard/DashboardContext';
import { useDashboardMenu } from '../../../stores/DashboardMenuStore';
import { useSessionStorage } from '../../../utilities/StorageUtilities';
import { UNAUTHORIZED_REDIRECT_COOKIE, URL_TRACKER_COOKIE } from '../../auth/AuthConstants';
import { useIsPgm } from '../../../stores/EnvironmentStore';

export default function Page({ id, isAdminPage, children }) {
    const [, /* Not Used */ uiAlertActions] = useUiAlerts();
    const [, /* Not used */ pageStateActions] = usePageState();
    const filterControlContextValue = useFilterControlContextValue();
    const pageRef = useRef();
    const pageSize = useSize(pageRef);
    const isPrinting = useIsPrinting();
    const [visiblePages] = useGroupPages();
    const { errorWrapper } = useErrorWrapper();
    const plmMainMenuPages = usePlmMainMenuPages();
    const { defaultPage } = useDefaultPage();
    const [dashboardMenu] = useDashboardMenu();
    const [pageContextValue, setPageContextValue] = useState({
        id,
    });
    const [canDrillIn] = useAllowDrillIn();
    const [unauthorizedRedirect] = useSessionStorage(UNAUTHORIZED_REDIRECT_COOKIE);
    const [lastLocation] = useSessionStorage(URL_TRACKER_COOKIE);
    const [isPgm] = useIsPgm();

    useEffect(() => {
        if (lastLocation && lastLocation.includes('no-access')) {
            sessionStorage.removeItem(URL_TRACKER_COOKIE);
        }
    }, [lastLocation]);

    // If we end up at a page as a result of an unauthorized redirect, show the info message letting the user know
    // what happened
    useEffect(() => {
        if (!isEmpty(unauthorizedRedirect)) {
            const attemptedMenuItem = Object.values(plmMainMenuPages).find(
                (menuPage) => menuPage.path === unauthorizedRedirect
            );

            const alertConfig = createAlertConfig(
                INFO,
                t`You do not have permissions to view the ${attemptedMenuItem.title} page.`
            );
            uiAlertActions.createAlert(alertConfig, { createOnNavigate: false });
            sessionStorage.removeItem('ngpUnauthorizedRedirect');
        }
    }, [unauthorizedRedirect, plmMainMenuPages, uiAlertActions]);

    // If a user attempts to view a page they do not have permissions to view, redirect them to root and display a warning informing them that
    // they do not have permissions. This will prevent users from accessing pages they cannot view through link sharing for example.
    useEffect(() => {
        if (!dashboardMenu.initialized) return;

        const availablePages = [
            ...Object.values(plmMainMenuPages).map((menuPage) => menuPage.path),
            NEW_DASHBOARD_ID,
            noAccessPageId,
        ];
        if (!isAdminPage && availablePages.includes(id)) {
            const allowedPages = [...visiblePages, NEW_DASHBOARD_ID, noAccessPageId];
            if (!allowedPages.includes(id)) {
                const attemptedMenuItem = Object.values(plmMainMenuPages).find((menuPage) => menuPage.path === id);

                // If the URL tracker cookie contains the value of the page that was just attempted, clear the cookie
                // so that attempts to access the root route in the future don't result in a warning.
                if (lastLocation && lastLocation.includes(attemptedMenuItem?.path)) {
                    sessionStorage.removeItem(URL_TRACKER_COOKIE);
                }

                return errorWrapper(
                    () =>
                        createAlertConfig(
                            INFO,
                            t`You do not have permissions to view the ${attemptedMenuItem?.title} page.`
                        ),
                    defaultPage
                )({});
            }
        }

        if (id.includes('-detail')) {
            if (!canDrillIn) {
                return errorWrapper(
                    () => createAlertConfig(INFO, t`You do not have permissions to view entity detail pages.`),
                    defaultPage
                )({});
            }
        }
    }, [
        id,
        isAdminPage,
        dashboardMenu,
        defaultPage,
        plmMainMenuPages,
        visiblePages,
        lastLocation,
        canDrillIn,
        errorWrapper,
    ]);

    useEffect(() => {
        setPageContextValue((currentValue) => {
            const newValue = {
                id,
                // Exclude the page size from the context value when printing.  This prevents
                // the context value from changing as the page renders, since that may cause
                // additional renders and impact render performance.  The pagesize is not
                // needed when printing.
                ...(isPrinting ? {} : { size: pageSize }),
            };

            return isEqual(currentValue, newValue) ? currentValue : newValue;
        });
    }, [id, pageSize, isPrinting]);

    useEffect(() => {
        pageStateActions.initialize(id);
    }, [id, pageStateActions]);

    return (
        <ErrorBoundary FallbackComponent={ErrorMessage}>
            <PageContext.Provider value={pageContextValue}>
                <FilterControlContext.Provider value={filterControlContextValue}>
                    <div ref={pageRef} className="flex-container">
                        {(dashboardMenu.initialized || isAdminPage || isPgm || isPrinting) && children}
                    </div>
                </FilterControlContext.Provider>
            </PageContext.Provider>
        </ErrorBoundary>
    );
}

Page.propTypes = {
    id: string.isRequired,
    isAdminPage: bool,
    children: node.isRequired,
};

Page.defaultProps = {
    isAdminPage: false,
};
